import {FC, useState} from 'react'
import clsx from 'clsx'
import {Modal} from 'react-bootstrap'
import {useParams} from 'react-router-dom'
import {KTSVG} from '../../../../../../template/helpers'
import {appsAPI} from '../../../../../../services/AppsService'
import {IAppsModel} from '../../../../../../types/apps/AppsModel'
import {IAmoCRMUserModel, IAmoCRMUserUpdateModel} from '../../../../../../types/apps/crm/amocrm/AmoCRMUserModel'
import {useIntl} from 'react-intl'

type Props = {
  user: IAmoCRMUserModel
}

const OneUserInServiceAmoCRM: FC<Props> = ({user}) => {
  const intl = useIntl()
  const {appId} = useParams()
  const [updateUser, setUpdateUser] = useState(false)
  const [updateApp, {isLoading}] = appsAPI.useUpdateAppMutation()
  const [internalNumber, setInternalNumber] = useState<string>(user.internal_number)
  const [userIsActive, setUserIsActive] = useState<boolean>(user.is_active)
  const [autoAnswer, setAutoAnswer] = useState<boolean>(user.auto_answer)
  const [multiplyRegister, setMultiplyRegister] = useState<boolean>(user.multiple_registration)
  const [smartCall, setSmartCall] = useState<boolean>(user.smart_call)
  const [showModalSmartCall, setShowModalSmartCall] = useState<boolean>(false)
  const [smartCallTime, setSmartCallTime] = useState(user.smart_call_time)

  const handleSave = async () => {
    const updatedUser: IAmoCRMUserUpdateModel = {
      id: user.id,
      is_active: userIsActive,
      amocrm_user_id: user.amocrm_user_id,
      internal_number: internalNumber,
      fullname: user.fullname,
      smart_call: smartCall,
      smart_call_time: smartCallTime,
      auto_answer: autoAnswer,
      multiple_registration: multiplyRegister,
    }

    const data = {
      id: appId!,
      settings: {
        amocrm: {
          users: [updatedUser],
        },
      },
    }
    await updateApp(data as IAppsModel)
    setUpdateUser(false)
  }

  const handleCancel = () => {
    setUserIsActive(user.is_active)
    setInternalNumber(user.internal_number)
    setSmartCall(user.smart_call)
    setSmartCallTime(user.smart_call_time)
    setAutoAnswer(user.auto_answer)
    setMultiplyRegister(user.multiple_registration)
    setUpdateUser(false)
  }

  const handleUserIsActive = () => {
    if (!userIsActive && internalNumber !== '') {
      setUserIsActive(true)
    } else {
      setUserIsActive(false)
    }
  }

  const handleEditInternalNumber = (value: string) => {
    setInternalNumber(value)
    if (value === '') {
      setUserIsActive(false)
    }
  }

  const handleAutoAnswer = () => {
    setAutoAnswer(!autoAnswer)
    setMultiplyRegister(false)
  }

  const handleMultiplyRegister = () => {
    setMultiplyRegister(!multiplyRegister)
    setAutoAnswer(false)
  }

  return (
    <tr role='row' className='text-center'>
      <td role='cell' className='min-w-70px'>
        <div className='d-flex justify-content-end'>
          <div className='form-check form-check-custom form-check-solid form-check-sm d-block'>
            <input
              className='form-check-input align-self-center'
              type='checkbox'
              checked={userIsActive}
              disabled={!updateUser}
              onClick={handleUserIsActive}
            />
          </div>
        </div>
      </td>
      <td role='cell' className='min-w-100px pe-0 px-0'>
        <input
          className='form-control form-control-sm form-control-flush text-gray-900 text-center'
          value={user.amocrm_user_id}
          disabled
          readOnly
        />
      </td>
      <td role='cell' className='min-w-150px pe-0 px-0'>
        <input
          className='form-control form-control-sm form-control-flush text-center'
          value={user.fullname}
          disabled
          readOnly
        />
      </td>
      <td role='cell' className='min-w-150px pe-0 px-0'>
        {!updateUser ? (
          <input
            className='form-control form-control-sm form-control-flush text-center'
            value={internalNumber}
            disabled
            readOnly
          />
        ) : (
          <input
            className={clsx('form-control form-control-sm form-control-flush text-center  rounded-4', {
              'bg-light-danger': internalNumber === '',
            })}
            type='number'
            value={internalNumber}
            onChange={(e) => handleEditInternalNumber(e.target.value)}
          />
        )}
      </td>
      <td role='cell' className='min-w-100px px-0 d-flex justify-content-center'>
        <div className='form-check form-check-custom form-check-solid form-check-sm d-block me-2 align-self-center'>
          <input
            className='form-check-input'
            type='checkbox'
            checked={smartCall}
            disabled={!updateUser}
            onClick={() => setSmartCall(!smartCall)}
          />
        </div>
        <button
          type='button'
          className='btn btn-icon btn-light-primary btn-sm align-self-center'
          onClick={() => setShowModalSmartCall(true)}
          disabled={!updateUser || !smartCall}
        >
          <KTSVG path='/media/icons/duotune/general/gen013.svg' className='align-self-center' />
        </button>
      </td>
      <td role='cell' className='min-w-70px pe-0 px-0'>
        <div className='form-check form-check-custom form-check-solid form-check-sm d-block'>
          <input
            className='form-check-input'
            type='checkbox'
            checked={autoAnswer}
            disabled={!updateUser}
            onClick={handleAutoAnswer}
          />
        </div>
      </td>
      <td role='cell' className='min-w-70px pe-0 px-0'>
        <div className='form-check form-check-custom form-check-solid form-check-sm d-block'>
          <input
            className='form-check-input'
            type='checkbox'
            checked={multiplyRegister}
            disabled={!updateUser}
            onClick={handleMultiplyRegister}
          />
        </div>
      </td>
      <td role='cell' className='min-w-100px pe-0 px-0'>
        {!updateUser ? (
          <button
            onClick={() => {
              setUpdateUser(true)
            }}
            className='btn btn-icon btn-light-primary btn-sm me-1 align-self-center'
          >
            <KTSVG path='/media/icons/duotune/art/art005.svg' className='align-self-center' />
          </button>
        ) : (
          <>
            <button
              className='btn btn-icon btn-light-success btn-active-color-light btn-sm me-1 align-self-center'
              disabled={isLoading}
              onClick={handleSave}
            >
              {!isLoading && <KTSVG path='/media/icons/duotune/general/gen037.svg' className='align-self-center' />}
              {isLoading && (
                <span className='indicator-progress' style={{display: 'block'}}>
                  <span className='spinner-border spinner-border-sm align-middle'></span>
                </span>
              )}
            </button>
            <button
              className='btn btn-icon btn-light-primary btn-active-color-light btn-sm me-1 align-self-center'
              onClick={handleCancel}
              disabled={isLoading}
              hidden={isLoading}
            >
              {!isLoading && <KTSVG path='/media/icons/duotune/general/gen034.svg' className='align-self-center' />}
            </button>
            <Modal show={showModalSmartCall} onHide={() => setShowModalSmartCall(false)}>
              <Modal.Header>
                <Modal.Title>
                  <h2>{intl.formatMessage({id: 'APPS.SETTINGS.SMARTCALLTIME.TITTLE'})}</h2>
                </Modal.Title>
                <div
                  className='btn btn-icon btn-sm btn-active-light-primary ms-2'
                  onClick={() => setShowModalSmartCall(false)}
                >
                  <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon svg-icon-2x' />
                </div>
              </Modal.Header>
              <Modal.Body>
                <table className='table align-middle table-row-dashed fs-7 gy-5 dataTable no-footer'>
                  <thead>
                    <tr className='text-center fs-9'>
                      <th role='columnheader' className='min-w-70px'>
                        {intl.formatMessage({id: 'APPS.SETTINGS.SMARTCALLTIME.ACTIVE'})}
                      </th>
                      <th role='columnheader' className='min-w-70px'>
                        {intl.formatMessage({id: 'APPS.SETTINGS.SMARTCALLTIME.DAY'})}
                      </th>
                      <th role='columnheader' className='min-w-150px'>
                        {intl.formatMessage({id: 'APPS.SETTINGS.SMARTCALLTIME.STARTTIME'})} —{' '}
                        {intl.formatMessage({id: 'APPS.SETTINGS.SMARTCALLTIME.ENDTIME'})}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr role='row' className='text-center'>
                      <td role='cell' className='min-w-70px pe-0'>
                        <div className='d-flex justify-content-center'>
                          <div className='form-check form-check-custom form-check-solid'>
                            <input
                              className='form-check-input align-self-center'
                              type='checkbox'
                              defaultChecked={smartCallTime.mon_is_active}
                              onChange={() =>
                                setSmartCallTime((prev) => ({
                                  ...prev,
                                  mon_is_active: !prev.mon_is_active,
                                }))
                              }
                            />
                          </div>
                        </div>
                      </td>
                      <td role='cell' className='min-w-70px pe-0 text-uppercase text-gray-800'>
                        {intl.formatMessage({id: 'APPS.SETTINGS.SMARTCALLTIME.MONDAY'})}
                      </td>
                      <td role='cell' className='d-flex min-w-150px pe-0 align-items-center'>
                        <input
                          className='max-w-70px form-control form-control-sm form-control-flush text-center text-gray-900'
                          defaultValue={smartCallTime.mon_start_time.slice(0, 5)}
                          onChange={(e) =>
                            setSmartCallTime((prev) => ({
                              ...prev,
                              mon_start_time: e.target.value + ':00',
                            }))
                          }
                        />
                        —
                        <input
                          className='max-w-70px form-control form-control-sm form-control-flush text-center text-gray-900'
                          defaultValue={smartCallTime.mon_end_time.slice(0, 5)}
                          onChange={(e) =>
                            setSmartCallTime((prev) => ({
                              ...prev,
                              mon_end_time: e.target.value + ':00',
                            }))
                          }
                        />
                      </td>
                    </tr>
                    <tr role='row' className='text-center'>
                      <td role='cell' className='min-w-70px pe-0'>
                        <div className='d-flex justify-content-center'>
                          <div className='form-check form-check-custom form-check-solid'>
                            <input
                              className='form-check-input align-self-center'
                              type='checkbox'
                              defaultChecked={smartCallTime.tue_is_active}
                              onChange={() =>
                                setSmartCallTime((prev) => ({
                                  ...prev,
                                  tue_is_active: !prev.tue_is_active,
                                }))
                              }
                            />
                          </div>
                        </div>
                      </td>
                      <td role='cell' className='min-w-70px pe-0 text-uppercase text-gray-800'>
                        {intl.formatMessage({id: 'APPS.SETTINGS.SMARTCALLTIME.TUESDAY'})}
                      </td>
                      <td role='cell' className='d-flex min-w-150px pe-0 align-items-center'>
                        <input
                          className='max-w-70px form-control form-control-sm form-control-flush text-center text-gray-900'
                          defaultValue={smartCallTime.tue_start_time.slice(0, 5)}
                          onChange={(e) =>
                            setSmartCallTime((prev) => ({
                              ...prev,
                              tue_start_time: e.target.value + ':00',
                            }))
                          }
                        />
                        —
                        <input
                          className='max-w-70px form-control form-control-sm form-control-flush text-center text-gray-900'
                          defaultValue={smartCallTime.tue_end_time.slice(0, 5)}
                          onChange={(e) =>
                            setSmartCallTime((prev) => ({
                              ...prev,
                              tue_end_time: e.target.value + ':00',
                            }))
                          }
                        />
                      </td>
                    </tr>
                    <tr role='row' className='text-center'>
                      <td role='cell' className='min-w-70px pe-0'>
                        <div className='d-flex justify-content-center'>
                          <div className='form-check form-check-custom form-check-solid'>
                            <input
                              className='form-check-input align-self-center'
                              type='checkbox'
                              defaultChecked={smartCallTime.wed_is_active}
                              onChange={() =>
                                setSmartCallTime((prev) => ({
                                  ...prev,
                                  wed_is_active: !prev.wed_is_active,
                                }))
                              }
                            />
                          </div>
                        </div>
                      </td>
                      <td role='cell' className='min-w-70px pe-0 text-uppercase text-gray-800'>
                        {intl.formatMessage({id: 'APPS.SETTINGS.SMARTCALLTIME.WEDNESDAY'})}
                      </td>
                      <td role='cell' className='d-flex min-w-150px pe-0 align-items-center'>
                        <input
                          className='max-w-70px form-control form-control-sm form-control-flush text-center text-gray-900'
                          defaultValue={smartCallTime.wed_start_time.slice(0, 5)}
                          onChange={(e) =>
                            setSmartCallTime((prev) => ({
                              ...prev,
                              wed_start_time: e.target.value + ':00',
                            }))
                          }
                        />
                        —
                        <input
                          className='max-w-70px form-control form-control-sm form-control-flush text-center text-gray-900'
                          defaultValue={smartCallTime.wed_end_time.slice(0, 5)}
                          onChange={(e) =>
                            setSmartCallTime((prev) => ({
                              ...prev,
                              wed_end_time: e.target.value + ':00',
                            }))
                          }
                        />
                      </td>
                    </tr>
                    <tr role='row' className='text-center'>
                      <td role='cell' className='min-w-70px pe-0'>
                        <div className='d-flex justify-content-center'>
                          <div className='form-check form-check-custom form-check-solid'>
                            <input
                              className='form-check-input align-self-center'
                              type='checkbox'
                              defaultChecked={smartCallTime.thu_is_active}
                              onChange={() =>
                                setSmartCallTime((prev) => ({
                                  ...prev,
                                  thu_is_active: !prev.thu_is_active,
                                }))
                              }
                            />
                          </div>
                        </div>
                      </td>
                      <td role='cell' className='min-w-70px pe-0 text-uppercase text-gray-800'>
                        {intl.formatMessage({id: 'APPS.SETTINGS.SMARTCALLTIME.THURSDAY'})}
                      </td>
                      <td role='cell' className='d-flex min-w-150px pe-0 align-items-center'>
                        <input
                          className='max-w-70px form-control form-control-sm form-control-flush text-center text-gray-900'
                          defaultValue={smartCallTime.thu_start_time.slice(0, 5)}
                          onChange={(e) =>
                            setSmartCallTime((prev) => ({
                              ...prev,
                              thu_start_time: e.target.value + ':00',
                            }))
                          }
                        />
                        —
                        <input
                          className='max-w-70px form-control form-control-sm form-control-flush text-center text-gray-900'
                          defaultValue={smartCallTime.thu_end_time.slice(0, 5)}
                          onChange={(e) =>
                            setSmartCallTime((prev) => ({
                              ...prev,
                              thu_end_time: e.target.value + ':00',
                            }))
                          }
                        />
                      </td>
                    </tr>
                    <tr role='row' className='text-center'>
                      <td role='cell' className='min-w-70px pe-0'>
                        <div className='d-flex justify-content-center'>
                          <div className='form-check form-check-custom form-check-solid'>
                            <input
                              className='form-check-input align-self-center'
                              type='checkbox'
                              defaultChecked={smartCallTime.fri_is_active}
                              onChange={() =>
                                setSmartCallTime((prev) => ({
                                  ...prev,
                                  fri_is_active: !prev.fri_is_active,
                                }))
                              }
                            />
                          </div>
                        </div>
                      </td>
                      <td role='cell' className='min-w-70px pe-0 text-uppercase text-gray-800'>
                        {intl.formatMessage({id: 'APPS.SETTINGS.SMARTCALLTIME.FRIDAY'})}
                      </td>
                      <td role='cell' className='d-flex min-w-150px pe-0 align-items-center'>
                        <input
                          className='max-w-70px form-control form-control-sm form-control-flush text-center text-gray-900'
                          defaultValue={smartCallTime.fri_start_time.slice(0, 5)}
                          onChange={(e) =>
                            setSmartCallTime((prev) => ({
                              ...prev,
                              fri_start_time: e.target.value + ':00',
                            }))
                          }
                        />
                        —
                        <input
                          className='max-w-70px form-control form-control-sm form-control-flush text-center text-gray-900'
                          defaultValue={smartCallTime.fri_end_time.slice(0, 5)}
                          onChange={(e) =>
                            setSmartCallTime((prev) => ({
                              ...prev,
                              fri_end_time: e.target.value + ':00',
                            }))
                          }
                        />
                      </td>
                    </tr>
                    <tr role='row' className='text-center'>
                      <td role='cell' className='min-w-70px pe-0'>
                        <div className='d-flex justify-content-center'>
                          <div className='form-check form-check-custom form-check-solid'>
                            <input
                              className='form-check-input align-self-center'
                              type='checkbox'
                              defaultChecked={smartCallTime.sat_is_active}
                              onChange={() =>
                                setSmartCallTime((prev) => ({
                                  ...prev,
                                  sat_is_active: !prev.sat_is_active,
                                }))
                              }
                            />
                          </div>
                        </div>
                      </td>
                      <td role='cell' className='min-w-70px pe-0 text-uppercase text-gray-800'>
                        {intl.formatMessage({id: 'APPS.SETTINGS.SMARTCALLTIME.SATURDAY'})}
                      </td>
                      <td role='cell' className='d-flex min-w-150px pe-0 align-items-center'>
                        <input
                          className='max-w-70px form-control form-control-sm form-control-flush text-center text-gray-900'
                          defaultValue={smartCallTime.sat_start_time.slice(0, 5)}
                          onChange={(e) =>
                            setSmartCallTime((prev) => ({
                              ...prev,
                              sat_start_time: e.target.value + ':00',
                            }))
                          }
                        />
                        —
                        <input
                          className='max-w-70px form-control form-control-sm form-control-flush text-center text-gray-900'
                          defaultValue={smartCallTime.sat_end_time.slice(0, 5)}
                          onChange={(e) =>
                            setSmartCallTime((prev) => ({
                              ...prev,
                              sat_end_time: e.target.value + ':00',
                            }))
                          }
                        />
                      </td>
                    </tr>
                    <tr role='row' className='text-center'>
                      <td role='cell' className='min-w-70px pe-0'>
                        <div className='d-flex justify-content-center'>
                          <div className='form-check form-check-custom form-check-solid'>
                            <input
                              className='form-check-input align-self-center'
                              type='checkbox'
                              defaultChecked={smartCallTime.sun_is_active}
                              onChange={() =>
                                setSmartCallTime((prev) => ({
                                  ...prev,
                                  sun_is_active: !prev.sun_is_active,
                                }))
                              }
                            />
                          </div>
                        </div>
                      </td>
                      <td role='cell' className='min-w-70px pe-0 text-uppercase text-gray-800'>
                        {intl.formatMessage({id: 'APPS.SETTINGS.SMARTCALLTIME.SUNDAY'})}
                      </td>
                      <td role='cell' className='d-flex min-w-150px pe-0 align-items-center'>
                        <input
                          className='max-w-70px form-control form-control-sm form-control-flush text-center text-gray-900'
                          defaultValue={smartCallTime.sun_start_time.slice(0, 5)}
                          onChange={(e) =>
                            setSmartCallTime((prev) => ({
                              ...prev,
                              sun_start_time: e.target.value + ':00',
                            }))
                          }
                        />
                        —
                        <input
                          className='max-w-70px form-control form-control-sm form-control-flush text-center text-gray-900'
                          defaultValue={smartCallTime.sun_end_time.slice(0, 5)}
                          onChange={(e) =>
                            setSmartCallTime((prev) => ({
                              ...prev,
                              sun_end_time: e.target.value + ':00',
                            }))
                          }
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </Modal.Body>
              <Modal.Footer>
                <div className='card-footer d-flex justify-content-end py-2 px-1 border-0'>
                  <button
                    type='button'
                    className='btn btn-sm btn-light-primary align-self-center'
                    onClick={() => setShowModalSmartCall(false)}
                  >
                    {intl.formatMessage({id: 'BUTTON.CLOSE'})}
                  </button>
                </div>
              </Modal.Footer>
            </Modal>
          </>
        )}
      </td>
    </tr>
  )
}

export {OneUserInServiceAmoCRM}
